import { createSlice } from "@reduxjs/toolkit";

import { type TAnalysisState } from "../../types/analysis.type";
import {
	extractLitheQuestions,
	extractQuestions,
} from "../../utils/analysisUtils";

import {
	getPointByRunIdThunk,
	getQuestionsForAnalysisThunk,
	getSimulationQuestionsThunk,
	getTapeDataForAnalysisThunk,
	getTapeStatsThunk,
} from "./analysis.thunk";

const initialState: TAnalysisState = {
	loanFilterByPoint: {},
	requestIds: {
		tape: null,
		stats: null,
		run: null,
		questions: null,
		simulation: null,
	},
	tapeStats: {
		data: null,
		loading: false,
		error: null,
	},
	tapeData: {
		data: null,
		loading: false,
		error: null,
	},
	valuationRun: {
		data: [],
		loading: false,
		error: null,
	},
	allPointData: {
		data: null,
		loading: false,
		error: null,
	},
	pointData: {
		data: null,
		loading: false,
		error: null,
	},
	jsonParameters: {
		data: null,
		loading: false,
		error: null,
	},
	simulationParameters: {
		data: null,
		loading: false,
		error: null,
	},
};

/**
 * Redux slice for managing analysis state.
 */
export const analysisState = createSlice({
	name: "analysis",
	initialState,
	reducers: {
		resetAnalysis: () => initialState,
		clearSelectedRun: (state) => {
			state.requestIds.run = null;
			state.requestIds.questions = null;
			state.requestIds.simulation = null;
			state.allPointData = { data: null, loading: false, error: null };
			state.pointData = { data: null, loading: false, error: null };
			state.jsonParameters = { data: null, loading: false, error: null };
			state.simulationParameters = { data: null, loading: false, error: null };
		},
		clearSelectedPoint: (state) => {
			state.requestIds.questions = null;
			state.pointData = { data: null, loading: true, error: null };
			state.jsonParameters = { data: null, loading: false, error: null };
		},
		updatePointData: (state, action) => {
			state.pointData = {
				data: action.payload,
				loading: false,
				error: null,
			};
		},
		updatePointWithError: (state, action) => {
			state.pointData = {
				...state.pointData,
				error: action.payload,
				loading: false,
			};
		},
		togglePointDataLoading: (state, action) => {
			state.pointData = {
				...state.pointData,
				loading: action.payload,
			};
		},
		updateParameter: (state, action) => {
			const data = action.payload;

			const globalParameters = extractQuestions(
				data.global.normal_questions
			);

			const actions = data.actions.map(
				(actionData: any) => {
					const parameters = extractQuestions(
						actionData.normal_questions
					);
					let litheData = null;

					if (actionData.lithe_question) {
						litheData = extractLitheQuestions(
							actionData.lithe_question,
							actionData.meta.action_id
						);
					}

					return {
						parameters,
						meta: {
							displayName: actionData.meta.display_name,
							actionId: actionData.meta.action_id,
						},
						litheData,
					};
				}
			);

			state.jsonParameters = {
				data: {
					globalParameters,
					actions,
					isConfirmed: data.is_confirmed,
					loanFilter: state.jsonParameters.data?.loanFilter ?? "",
				},
				loading: false,
				error: null,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(
				getTapeDataForAnalysisThunk.pending,
				(state, action) => {
					state.requestIds.tape = action.meta.requestId;
					state.requestIds.run = null;
					state.requestIds.questions = null;
					state.requestIds.simulation = null;
					state.allPointData = { data: null, loading: false, error: null };
					state.pointData = { data: null, loading: false, error: null };
					state.jsonParameters = { data: null, loading: false, error: null };
					state.simulationParameters = { data: null, loading: false, error: null };
					state.tapeData = {
						data: null,
						loading: true,
						error: null,
					};
					state.valuationRun = {
						data: [],
						loading: true,
						error: null,
					};
				}
			)
			.addCase(
				getTapeDataForAnalysisThunk.fulfilled,
				(state, action) => {
					if (state.requestIds.tape !== action.meta.requestId) return;
					state.tapeData = {
						data: action.payload.tape,
						loading: false,
						error: null,
					};
					state.valuationRun = {
						data: action.payload.tape.valuation_runs,
						loading: false,
						error: null,
					};
				}
			)
			.addCase(
				getTapeDataForAnalysisThunk.rejected,
				(state, action) => {
					if (state.requestIds.tape !== action.meta.requestId) return;
					state.tapeData = {
						data: null,
						loading: false,
						error: action.payload,
					};
					state.valuationRun = {
						data: [],
						loading: false,
						error: action.payload,
					};
				}
			);

		builder
			.addCase(getPointByRunIdThunk.pending, (state, action) => {
				state.requestIds.run = action.meta.requestId;
				state.requestIds.questions = null;
				state.requestIds.simulation = null;
				state.pointData = { data: null, loading: true, error: null };
				state.jsonParameters = { data: null, loading: false, error: null };
				state.simulationParameters = { data: null, loading: false, error: null };
				state.allPointData = {
					data: null,
					loading: true,
					error: null,
				};
			})
			.addCase(
				getPointByRunIdThunk.fulfilled,
				(state, action) => {
					if (state.requestIds.run !== action.meta.requestId) return;
					state.allPointData = {
						data: action.payload,
						loading: false,
						error: null,
					};
				}
			)
			.addCase(
				getPointByRunIdThunk.rejected,
				(state, action) => {
					if (state.requestIds.run !== action.meta.requestId) return;
					state.allPointData = {
						data: null,
						loading: false,
						error: action.payload,
					};
				}
			);
		builder
			.addCase(getTapeStatsThunk.pending, (state, action) => {
				state.requestIds.stats = action.meta.requestId;
				state.tapeStats = {
					data: null,
					loading: true,
					error: null,
				};
			})
			.addCase(
				getTapeStatsThunk.fulfilled,
				(state, action) => {
					if (state.requestIds.stats !== action.meta.requestId) return;
					state.tapeStats = {
						data: action.payload,
						loading: false,
						error: null,
					};
				}
			)
			.addCase(
				getTapeStatsThunk.rejected,
				(state, action) => {
					if (state.requestIds.stats !== action.meta.requestId) return;
					state.tapeStats = {
						data: null,
						loading: false,
						error: action.payload,
					};
				}
			);
		builder
			.addCase(
				getQuestionsForAnalysisThunk.pending,
				(state, action) => {
					state.requestIds.questions = action.meta.requestId;
					state.jsonParameters = {
						data: null,
						loading: true,
						error: null,
					};
				}
			)
			.addCase(
				getQuestionsForAnalysisThunk.fulfilled,
				(state, action) => {
					if (state.requestIds.questions !== action.meta.requestId) return;
					const data = action.payload;
					const pointId = action.meta.arg;
					const loanFilter = data.loan_filter ?? "";
					const previousFilter = state.loanFilterByPoint[pointId];
					// The first response is the local baseline; later responses reveal Paul edits.
					const observedChange = Boolean(
						previousFilter &&
							(previousFilter.observedChange ||
								loanFilter !== previousFilter.lastSeen)
					);
					const baseline =
						data.is_confirmed || !previousFilter
							? loanFilter
							: previousFilter.baseline;
					state.loanFilterByPoint[pointId] = {
						baseline,
						lastSeen: loanFilter,
						pending: !data.is_confirmed && loanFilter !== baseline,
						observedChange: !data.is_confirmed && observedChange,
					};

					const globalParameters = extractQuestions(
						data.global.normal_questions
					);

					const actions = data.actions.map(
						(actionData: any) => {
							const parameters = extractQuestions(
								actionData.normal_questions
							);
							let litheData = null;

							if (actionData.lithe_question) {
								litheData = extractLitheQuestions(
									actionData.lithe_question,
									actionData.meta.action_id
								);
							}

							return {
								parameters,
								meta: {
									displayName: actionData.meta.display_name,
									actionId: actionData.meta.action_id,
								},
								litheData,
							};
						}
					);

					state.jsonParameters = {
						data: {
							globalParameters,
							actions,
							isConfirmed: data.is_confirmed,
							loanFilter,
						},
						loading: false,
						error: null,
					};
				}
			)
			.addCase(
				getQuestionsForAnalysisThunk.rejected,
				(state, action) => {
					if (state.requestIds.questions !== action.meta.requestId) return;
					state.jsonParameters = {
						data: null,
						loading: false,
						error: action.payload,
					};
				}
			);
		builder
			.addCase(
				getSimulationQuestionsThunk.pending,
				(state, action) => {
					state.requestIds.simulation = action.meta.requestId;
					state.simulationParameters = {
						data: null,
						loading: true,
						error: null,
					};
				}
			)
			.addCase(
				getSimulationQuestionsThunk.fulfilled,
				(state, action) => {
					if (state.requestIds.simulation !== action.meta.requestId) return;
					const data = action.payload.questions;
					const parameters = extractQuestions(data);
					state.simulationParameters = {
						data: {
							parameters,
							isConfirmed: action.payload.is_confirmed,
						},
						loading: false,
						error: null,
					};
				}
			)
			.addCase(
				getSimulationQuestionsThunk.rejected,
				(state, action) => {
					if (state.requestIds.simulation !== action.meta.requestId) return;
					state.simulationParameters = {
						data: null,
						loading: false,
						error: action.payload,
					};
				}
			);
	},
});

export const AnalysisActions = analysisState.actions;
export const AnalysisReducer = analysisState.reducer;
